import React, { useEffect, useState } from "react";
import { Loader2, Search, Users } from "lucide-react";
import axiosInstance from "../services/axiosinstance";
import { apiUrl } from "../routes/apiRoutes";

const AllStudentsPage = () => {
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await axiosInstance.get(apiUrl.GET_ALL_STUDENTS);
        if (res.data?.status && Array.isArray(res.data?.data)) {
          setStudents(res.data.data);
        } else {
          setStudents([]);
          setError(res.data?.message || "Failed to load students");
        }
      } catch (err) {
        setError(err.response?.data?.message || "Failed to load students");
        setStudents([]);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const filtered = students.filter((s) => {
    const q = search.toLowerCase();
    return (
      s.name?.toLowerCase().includes(q) ||
      s.studentId?.toLowerCase().includes(q) ||
      s.email?.toLowerCase().includes(q) ||
      s.number?.includes(q)
    );
  });

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold text-slate-200">All Students</h2>
          <p className="text-xs text-slate-500 mt-0.5">
            {students.length} student{students.length !== 1 ? "s" : ""} total
          </p>
        </div>
      </div>

      <div className="relative max-w-sm">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by name, ID, email, or phone..."
          className="w-full pl-9 pr-3 py-2 bg-[#0d1220] text-slate-100 placeholder:text-slate-500 border border-[#1d3350] rounded-lg text-sm outline-none focus:ring-1 focus:ring-blue-500"
        />
      </div>

      {loading ? (
        <div className="flex items-center justify-center rounded-lg border border-[#1d3350] bg-[#0f1c2e] py-12">
          <Loader2 size={28} className="animate-spin text-slate-400" />
        </div>
      ) : error ? (
        <div className="rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-4 text-sm text-red-400">
          {error}
        </div>
      ) : filtered.length === 0 ? (
        <div className="flex flex-col items-center justify-center rounded-lg border border-[#1d3350] bg-[#0f1c2e] py-12 text-center">
          <Users size={32} className="text-slate-500" />
          <p className="mt-2 text-sm text-slate-500">No students found.</p>
        </div>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-[#1d3350]">
          <table className="w-full text-sm">
            <thead className="bg-[#0f1c2e] text-slate-400">
              <tr>
                <th className="px-4 py-2.5 text-left font-medium">Name</th>
                <th className="px-4 py-2.5 text-left font-medium">Student ID</th>
                <th className="px-4 py-2.5 text-left font-medium">Email</th>
                <th className="px-4 py-2.5 text-left font-medium">Phone</th>
                <th className="px-4 py-2.5 text-left font-medium">Courses</th>
                <th className="px-4 py-2.5 text-left font-medium">Batches</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#1d3350]">
              {filtered.map((s) => (
                <tr key={s._id} className="bg-[#0d1b2e]">
                  <td className="px-4 py-2.5 text-slate-200 capitalize">{s.name}</td>
                  <td className="px-4 py-2.5 text-slate-400 text-xs">{s.studentId}</td>
                  <td className="px-4 py-2.5 text-slate-400 text-xs">{s.email || "—"}</td>
                  <td className="px-4 py-2.5 text-slate-400 text-xs">{s.number || "—"}</td>
                  <td className="px-4 py-2.5 text-slate-400 text-xs">
                    {Array.isArray(s.course) && s.course.length > 0 ? (
                      <span className="rounded bg-[#13233a] px-2 py-0.5">
                        {s.course.length} course{s.course.length !== 1 ? "s" : ""}
                      </span>
                    ) : (
                      <span className="text-amber-400">No course</span>
                    )}
                  </td>
                  <td className="px-4 py-2.5 text-slate-400 text-xs">
                    {Array.isArray(s.batch) && s.batch.length > 0 ? (
                      <span className="rounded bg-[#13233a] px-2 py-0.5">
                        {s.batch.length} batch{s.batch.length !== 1 ? "es" : ""}
                      </span>
                    ) : (
                      <span className="text-amber-400">No batch</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default AllStudentsPage;